import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Calendar, Clock, Video, Download, Users, Mail, Phone } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { isUserAdmin } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';

interface BeyondTheMindRegistration {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  experience_level: string | null;
  questions: string | null;
  consent_marketing: boolean;
  event_slug: string;
  registered_at: string;
}

export function BeyondTheMindRegistrations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [registrations, setRegistrations] = useState<BeyondTheMindRegistration[]>([]);
  const [selectedReg, setSelectedReg] = useState<BeyondTheMindRegistration | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadRegistrations();
  }, [user, navigate]);

  const loadRegistrations = async () => {
    if (!user?.id) return;
    const isAdmin = await isUserAdmin(user.id);
    if (!isAdmin) {
      navigate('/login');
      return;
    }

    const { data, error: loadError } = await supabase
      .from('masterclass_registrations')
      .select('*')
      .eq('event_slug', 'beyond-the-mind')
      .order('registered_at', { ascending: false });

    if (loadError) {
      setError('We could not load the registrations right now.');
    } else {
      setRegistrations((data || []) as BeyondTheMindRegistration[]);
    }
    setIsLoading(false);
  };

  const filteredRegistrations = useMemo(() => registrations.filter((r) => {
    const matchesSearch = `${r.full_name} ${r.email} ${r.phone || ''} ${r.experience_level || ''}`
      .toLowerCase()
      .includes(search.toLowerCase());
    return matchesSearch;
  }), [registrations, search]);

  const downloadCSV = () => {
    const rows = [['Name', 'Email', 'Phone', 'Profession/Role', 'Questions', 'Marketing Consent', 'Registered At']];
    filteredRegistrations.forEach((r) => rows.push([
      r.full_name,
      r.email,
      r.phone || '',
      r.experience_level || '',
      r.questions || '',
      r.consent_marketing ? 'Yes' : 'No',
      new Date(r.registered_at).toLocaleString(),
    ]));
    const csv = rows.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'beyond-the-mind-registrations.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) return <LoadingSpinner message="Loading registrations..." />;

  return (
    <div className="min-h-screen bg-slate-900" style={{ fontFamily: 'Urbanist, sans-serif' }}>
      <header className="bg-white/85 backdrop-blur-sm border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-600">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="p-2.5 bg-teal-600 rounded-xl shadow-soft">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Beyond the Mind — Registrations</h1>
              <p className="text-sm text-slate-500">Live Session — October 23, 2026</p>
            </div>
          </div>
          <button
            onClick={downloadCSV}
            className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-xl transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && <div className="mb-6 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Registrations', value: registrations.length, icon: Users, color: 'bg-slate-100 text-slate-700' },
            { label: 'With Phone', value: registrations.filter((r) => r.phone).length, icon: Phone, color: 'bg-teal-50 text-teal-600' },
            { label: 'Marketing Consent', value: registrations.filter((r) => r.consent_marketing).length, icon: Mail, color: 'bg-amber-50 text-amber-600' },
            { label: 'With Questions', value: registrations.filter((r) => r.questions).length, icon: Calendar, color: 'bg-orange-50 text-orange-600' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl shadow-sm p-5 border border-slate-200">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${stat.color}`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <div className="text-2xl font-bold text-slate-800">{stat.value}</div>
              <div className="text-xs text-slate-500">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Event info banner */}
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-slate-200 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-teal-600" />
              <span className="text-sm font-medium text-slate-700">Friday, October 23, 2026</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-teal-600" />
              <span className="text-sm font-medium text-slate-700">11:00 AM US Eastern / 4:00 PM UK</span>
            </div>
            <div className="flex items-center gap-2">
              <Video className="w-4 h-4 text-teal-600" />
              <span className="text-sm font-medium text-slate-700">60 Minutes | Live via Zoom</span>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="flex gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, phone, or profession..."
              className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:border-teal-500"
            />
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_420px] gap-6 items-start">
          {/* List */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-slate-200">
            {filteredRegistrations.length === 0 ? (
              <div className="p-16 text-center text-slate-400 text-sm">
                {search ? 'No registrations match your search.' : 'No one has registered yet. Share the registration link to get started.'}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredRegistrations.map((r, index) => (
                  <motion.button
                    key={r.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    onClick={() => setSelectedReg(r)}
                    className={`w-full text-left p-5 hover:bg-slate-50 transition-colors ${selectedReg?.id === r.id ? 'bg-teal-50 border-l-4 border-teal-500' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-1">
                      <div className="font-semibold text-slate-800">{r.full_name}</div>
                      <span className="text-xs text-slate-400 whitespace-nowrap">
                        {new Date(r.registered_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-1">
                      <span>{r.email}</span>
                      {r.phone && (
                        <>
                          <span>•</span>
                          <span>{r.phone}</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      {r.experience_level && (
                        <span className="text-teal-600 font-medium">{r.experience_level}</span>
                      )}
                      {r.consent_marketing && (
                        <span className="text-amber-600 font-medium">Marketing consent</span>
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            )}
          </div>

          {/* Detail panel */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-slate-200 lg:sticky lg:top-24">
            {!selectedReg ? (
              <div className="py-16 text-center text-slate-400">
                <Users className="w-10 h-10 mx-auto mb-3 text-teal-400/40" />
                <p className="text-sm">Select a registration to view details.</p>
              </div>
            ) : (
              <div>
                <h2 className="text-xl font-bold text-slate-800 mb-1">{selectedReg.full_name}</h2>
                <p className="text-sm text-slate-500 mb-4">{selectedReg.email}</p>

                <div className="space-y-3 mb-5">
                  {selectedReg.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-700">{selectedReg.phone}</span>
                    </div>
                  )}
                  {selectedReg.experience_level && (
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-700">{selectedReg.experience_level}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-700">Registered {new Date(selectedReg.registered_at).toLocaleString()}</span>
                  </div>
                </div>

                {selectedReg.questions && (
                  <div className="px-4 py-4 rounded-xl bg-slate-50 border border-slate-200 mb-5">
                    <div className="flex items-center gap-2 mb-2 text-teal-600 text-xs font-bold uppercase tracking-wide">
                      Their Question
                    </div>
                    <p className="text-slate-700 leading-relaxed whitespace-pre-wrap text-sm">{selectedReg.questions}</p>
                  </div>
                )}

                <div className={`px-4 py-3 rounded-xl border flex items-center gap-2 ${selectedReg.consent_marketing ? 'bg-teal-50 border-teal-200 text-teal-600' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>
                  <Mail className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {selectedReg.consent_marketing ? 'Marketing consent given' : 'No marketing consent'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
